/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  GSuiteFolderType,
  LetterTemplateLevel,
  LetterTemplateRestService,
} from '@gh/builder-shared-data';
import {
  bindUniqueId,
  getRouteStaticData,
  InitViewService,
  mergeUniqueId,
} from '@gh/common-shared';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  getGSuiteFolderTypeForView,
  getLetterTemplateLevelId,
  LetterTemplate,
  letterTemplateToCopyDto,
  letterTemplateToSourceDto,
  LetterTemplateValidationResult,
} from './letter-template.model';

@Injectable()
export class LetterTemplateBusinessService implements InitViewService {
  constructor(private letterTemplateRest: LetterTemplateRestService,
              private userContext: BuilderUserContext) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const type = getRouteStaticData(route)['type'];

    return {
      templates: this.letterTemplateRest.findAll(type).pipe(map$((items) => items.map(bindUniqueId))),
    };
  }

  makeDraft(template: LetterTemplate, level: LetterTemplateLevel): Observable<LetterTemplate> {
    const sourceId = {
      [LetterTemplateLevel.Corporate]: template.corporateFileId,
      [LetterTemplateLevel.Master]: template.masterFileId,
    }[level];

    const destination = this.userContext.officeView ?
      GSuiteFolderType.OfficeTemplatesDraft
      : GSuiteFolderType.MasterZoneTemplatesDraft;

    return this.letterTemplateRest.copy(letterTemplateToCopyDto(template, sourceId!, destination)).pipe(
      map$((dto) => mergeUniqueId(template.uniqueId, dto)));
  }

  completeDraft(template: LetterTemplate): Observable<LetterTemplate> {
    const destination = getGSuiteFolderTypeForView(this.userContext.applicationView);

    return this.letterTemplateRest.copy(letterTemplateToCopyDto(template, template.draftFileId!, destination)).pipe(
      map$((dto) => mergeUniqueId(template.uniqueId, dto)));
  }

  pushDown(template: LetterTemplate): Observable<LetterTemplate> {
    const sourceId = template.masterFileId || template.corporateFileId;
    return this.letterTemplateRest.pushDown(letterTemplateToSourceDto(template, sourceId!)).pipe(
      map$(() => template));
  }

  validate(template: LetterTemplate,
           level: LetterTemplateLevel,
           isDraft: boolean): Observable<LetterTemplateValidationResult> {
    const sourceId = getLetterTemplateLevelId(template, level, isDraft);
    return this.letterTemplateRest.validate(letterTemplateToSourceDto(template, sourceId!));
  }
}
